import React, { useState } from "react";
import {
  Stack,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Menu,
  Modal,
  LinearProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MuiCloseIcon from "@mui/icons-material/Close";

import { ReactComponent as ActionIcon } from "../../../../../../vendor/_new/icon/sites/action.svg";
import { ReactComponent as EditIcon } from "../../../../../../vendor/_new/icon/sites/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../../../../vendor/_new/icon/sites/delete.svg";
import { ReactComponent as CloseIcon } from "../../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { tableHeadSx, tableBodySx } from "../../../detection/WAFEvent";
import { eventSearchInputSx } from "../../../detection/eventSearchInputSx";
import LayerdIcon from "../../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../../icons/uiLucideIcons";
import { AsecEventsFilterBar } from "../../api_security/components/AsecEventsFilterBar";

import { GreenSwitch, SafeTextField } from "../../../../../common/Greens";
import { getExpressionKeyTitle, ExceptionSkipRuleType } from "../../../../../../utils/constants";
import { generateMenuSx } from "../../../../../../layouts/_new/Normal";
import colors from "../../../../../../utils/colors";

import ConfigWAFExceptionSettingsModal from "./modal/ConfigWAFExceptionSettings";
import useAuth from "../../../../../../hooks/useAuth";
import useWAFConfig from "../../../../../../hooks/user/useWAFConfig";
import { CancelButton, PrimaryButton, SaveButton } from "../../../../../common/Button";

const getAction = (code) => {
  switch (code) {
    case ExceptionSkipRuleType.ALL:
      return "SKIP ALL";
    case ExceptionSkipRuleType.SMART_PARSE:
      return "SKIP SMART PARSE WAF";
    case ExceptionSkipRuleType.SIGNATURE:
      return "SKIP CRS rules";
    case ExceptionSkipRuleType.SENSEDEFENCE_SIGNATURE:
      return "SKIP Layerd SIG WAF";
    default:
      break;
  }
};

/** LE-366 — Sec Rule IDs: module text for non-signature; "All"/IDs for signature. */
function normalizeSkipRuleType(skipRuleType) {
  const n = Number(skipRuleType);
  if (Number.isNaN(n)) return skipRuleType;
  // Legacy type 1 remapped to ALL (same as ConfigWAFExceptionSettings).
  if (n === 1) return ExceptionSkipRuleType.ALL;
  return n;
}

function isSignatureSkipRuleType(skipRuleType) {
  const t = normalizeSkipRuleType(skipRuleType);
  return (
    t === ExceptionSkipRuleType.SIGNATURE
    || t === ExceptionSkipRuleType.SENSEDEFENCE_SIGNATURE
  );
}

function getExceptionSecRuleIdsDisplay(skipRuleType, skipSecruleIds) {
  const t = normalizeSkipRuleType(skipRuleType);
  if (!isSignatureSkipRuleType(t)) {
    if (t === ExceptionSkipRuleType.ALL) {
      return { kind: "text", text: "All rules" };
    }
    if (t === ExceptionSkipRuleType.SMART_PARSE) {
      return { kind: "text", text: "Not applicable" };
    }
    return { kind: "text", text: "Not available" };
  }
  const ids = Array.isArray(skipSecruleIds)
    ? skipSecruleIds.map((id) => String(id).trim()).filter(Boolean)
    : [];
  if (ids.length === 0) {
    return { kind: "all" };
  }
  return { kind: "ids", ids };
}

function exceptionMatchesSecRuleSearch(skipRuleType, skipSecruleIds, pattern) {
  const display = getExceptionSecRuleIdsDisplay(skipRuleType, skipSecruleIds);
  if (display.kind === "text") return display.text.search(pattern) >= 0;
  if (display.kind === "all") return "All".search(pattern) >= 0;
  return display.ids.join().search(pattern) >= 0;
}

const ConfigWAFExceptTabContent = ({ siteUid, canConfigureWAFForSite }) => {
  const { isAuthenticated, homeController, wafdashController, websiteController, wafeventController, planController } = useAuth();
  const { exceptions, getExceptions, updateException, setErr, deleteException } = useWAFConfig();

  const [pattern, setPattern] = React.useState("");

  const [isInitialized, setIsInitialized] = useState(true);
  React.useEffect(() => {
    if (isAuthenticated) {
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (websiteController) websiteController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();
      if (siteUid) {
        getExceptions(siteUid).then(() => setIsInitialized(false));
      }
    }
    setErr(null);
  }, [isAuthenticated, getExceptions, siteUid, setErr]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeExceptionEnable = (event, id) => {
    if (!canConfigureWAFForSite) return;
    const value = { enabled: event.target.checked };
    if (siteUid) {
      updateException(siteUid, id, value);
    }
  };

  const [actionMenu, setActionMenu] = useState({
    anchorEl: null, // the DOM element the menu anchors to
    exceptionId: null, // index of subrow (or null if main row)
  });
  // open handler (call from the ActionIcon onClick)
  const handleActionOpen = (event, exceptionId = null) => {
    event.stopPropagation(); // prevent row expand/click side effects
    setActionMenu({ anchorEl: event.currentTarget, exceptionId });
  };

  // close handler
  const handleActionClose = () => {
    setActionMenu({ anchorEl: null, exceptionId: null });
  };

  const isActionMenuOpen = Boolean(actionMenu.anchorEl);

  const [idToDelete, setIdToDelete] = useState();
  const [openExceptionToDelete, setOpenExceptionToDelete] = useState(false);
  const handleExceptionToDeleteOpen = () => setOpenExceptionToDelete(true);
  const handleExceptionToDeleteClose = () => setOpenExceptionToDelete(false);

  const [exceptionToEdit, setExceptionToEdit] = useState(null);
  const [openException, setOpenException] = useState(false);
  const handleExceptionOpen = () => setOpenException(true);
  const handleExceptionClose = () => {
    setOpenException(false);
    setExceptionToEdit(null);
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!canConfigureWAFForSite) return;
    setIsDeleting(true);
    await deleteException(siteUid, { exception_id: idToDelete });
    setIsDeleting(false);
    handleExceptionToDeleteClose();
  };

  return (
    <>
      {isInitialized && (
        <Box sx={{ width: "100%", position: "fixed", top: 66 }}>
          <LinearProgress
            sx={{
              "& .MuiLinearProgress-bar1Indeterminate": { backgroundColor: colors.primary[200] },
              "& .MuiLinearProgress-bar2Indeterminate": { backgroundColor: colors.primary[200] },
              height: 4,
            }}
          />
        </Box>
      )}
      <Stack spacing={6}>
        <AsecEventsFilterBar
          action={(
            <PrimaryButton
              startIcon={<LayerdIcon icon={UI_ICONS.plus} size={16} color="inherit" />}
              disabled={!canConfigureWAFForSite}
              onClick={() => {
                setExceptionToEdit(null);
                handleExceptionOpen();
              }}
              sx={{
                height: 36,
                minHeight: 36,
                minWidth: 180,
                px: 2,
                py: 0,
                fontSize: 14,
                lineHeight: "21px",
                fontWeight: 400,
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": { mr: 1 },
              }}
            >
              Add WAF exception
            </PrimaryButton>
          )}
        >
          <SafeTextField
            sanitize={false}
            placeholder="Search name, condition etc..."
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            inputProps={{ "aria-label": "Search name, condition etc." }}
            sx={eventSearchInputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    className="event-search-icon"
                    sx={{
                      fontSize: 16,
                      color: colors.primary[300],
                      transition: "color 140ms ease",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: pattern ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setPattern("")}
                    edge="end"
                    sx={{
                      p: 0.4,
                      borderRadius: "6px",
                      color: colors.primary[300],
                      transition: "background-color 120ms ease, color 120ms ease",
                      "&:hover": {
                        backgroundColor: "rgba(99, 113, 247, 0.12)",
                        color: colors.primary[300],
                      },
                    }}
                  >
                    <MuiCloseIcon sx={{ fontSize: 14, color: "inherit" }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </AsecEventsFilterBar>
        <Box sx={{ width: "100%", borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2 }}>
          <Table
            sx={{
              "& .MuiTableCell-head": { py: 3, px: 3 },
              "& .MuiTableCell-body": { py: 2, px: 3 },
              "& .MuiTableCell-root": { borderBottom: "none" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "10%" }}>
                  <Typography sx={tableHeadSx}>No</Typography>
                </TableCell>
                <TableCell sx={{ width: "30%" }}>
                  <Typography sx={tableHeadSx}>Description</Typography>
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  <Typography sx={tableHeadSx}>Action</Typography>
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  <Typography sx={tableHeadSx}>Sec Rule IDs</Typography>
                </TableCell>
                <TableCell sx={{ width: "10%" }}>
                  <Typography sx={tableHeadSx}>Status</Typography>
                </TableCell>
                <TableCell sx={{ width: "10%" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exceptions
                ?.filter((exception) => {
                  let showFlag = false;
                  if (pattern === undefined || pattern === null || pattern === "") showFlag = true;
                  else if ((exception?.name ? exception?.name : exception?.id)?.search(pattern) >= 0) showFlag = true;
                  else if (exception.fields && exception.fields.join().search(pattern) >= 0) showFlag = true;
                  else if (getAction(exception.skip_rule_type)?.search(pattern) >= 0) showFlag = true;
                  else if (exceptionMatchesSecRuleSearch(exception.skip_rule_type, exception.skip_secrule_ids, pattern)) showFlag = true;
                  return showFlag;
                })
                ?.map((exception, index) => (
                  <TableRow key={`exception_${index}`}>
                    <TableCell>
                      <Typography sx={tableBodySx}>{index + 1}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{exception?.name ? exception?.name : exception?.id}</Typography>
                      <Typography sx={tableBodySx}>
                        {exception?.fields && exception?.fields.map((field) => getExpressionKeyTitle(field)).join(", ")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{getAction(exception?.skip_rule_type)}</Typography>
                    </TableCell>
                    <TableCell align="left" sx={{ padding: "8px" }}>
                      {(() => {
                        const display = getExceptionSecRuleIdsDisplay(
                          exception?.skip_rule_type,
                          exception?.skip_secrule_ids,
                        );
                        if (display.kind === "text") {
                          return <Typography sx={tableBodySx}>{display.text}</Typography>;
                        }
                        if (display.kind === "all") {
                          return <Typography sx={tableBodySx}>All</Typography>;
                        }
                        return display.ids.map((id) => (
                          <Typography key={id} sx={tableBodySx}>
                            {id}
                          </Typography>
                        ));
                      })()}
                    </TableCell>
                    <TableCell>
                      <GreenSwitch
                        checked={exception?.enabled}
                        disabled={!canConfigureWAFForSite}
                        onChange={(e) => changeExceptionEnable(e, exception?.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                        <Box
                          onClick={(e) => canConfigureWAFForSite && handleActionOpen(e, exception?.id)}
                          sx={{
                            cursor: canConfigureWAFForSite ? "pointer" : "not-allowed",
                            width: 24,
                            height: 24,
                            opacity: canConfigureWAFForSite ? 1 : 0.4,
                          }}
                        >
                          <ActionIcon style={{ color: colors.neutral[600] }} />
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              {exceptions?.filter((exception) => {
                let showFlag = false;
                if (pattern === undefined || pattern === null || pattern === "") showFlag = true;
                else if ((exception?.name ? exception?.name : exception?.id)?.search(pattern) >= 0) showFlag = true;
                else if (exception.fields && exception.fields.join().search(pattern) >= 0) showFlag = true;
                else if (getAction(exception.skip_rule_type)?.search(pattern) >= 0) showFlag = true;
                else if (exceptionMatchesSecRuleSearch(exception.skip_rule_type, exception.skip_secrule_ids, pattern)) showFlag = true;
                return showFlag;
              })?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography sx={tableBodySx} textAlign="center">
                      There are no exceptions
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Stack>
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={isActionMenuOpen}
        onClose={handleActionClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 200,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { exceptionId } = actionMenu;
              setExceptionToEdit(exceptions?.find((exception) => exception?.id === exceptionId));
              handleActionClose();
              handleExceptionOpen();
            }}
            sx={generateMenuSx(176, 37)}
          >
            <EditIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Edit</Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { exceptionId } = actionMenu;
              setIdToDelete(exceptionId);
              handleExceptionToDeleteOpen();
              handleActionClose();
            }}
            sx={generateMenuSx(176, 37)}
          >
            <DeleteIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Delete
            </Typography>
          </Stack>
        </Box>
      </Menu>
      <Modal open={openExceptionToDelete} onClose={handleExceptionToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Exception?
            </Typography>
            <Box onClick={handleExceptionToDeleteClose} sx={{ cursor: "pointer" }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            Are you sure you want to permanently delete this WAF exception?
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleExceptionToDeleteClose} outlined={true} />
            <SaveButton onClick={handleDelete} disabled={isDeleting || !canConfigureWAFForSite} label="Delete" />
          </Stack>
        </Stack>
      </Modal>
      <ConfigWAFExceptionSettingsModal
        siteUid={siteUid}
        open={openException}
        handleClose={handleExceptionClose}
        exceptionToEdit={exceptionToEdit}
        canConfigureWAFForSite={canConfigureWAFForSite}
      />
    </>
  );
};
export default ConfigWAFExceptTabContent;
